import { createApi } from '@reduxjs/toolkit/query/react';
import Papa from 'papaparse';
import { MARKET_DATA_API } from 'services/constants';
import { rawBaseQuery } from 'services/shared';
import {
  AssetClassesApiOptions,
  AssetClassApiResponse,
  AssetClassesFactorsApiOptions,
  AssetFactorsApiOptions,
  AssetFactorsSeries,
} from 'services/types';

export const marketDataApi = createApi({
  reducerPath: MARKET_DATA_API,
  baseQuery: rawBaseQuery,
  endpoints: (builder) => ({
    getAssetClasses: builder.query<
      AssetClassApiResponse[],
      AssetClassesApiOptions
    >({
      query: ({ asOfDate }) =>
        encodeURI(`/marketData/assetClasses?asOfDate=${asOfDate}`),
    }),
    getAssetClassFactors: builder.query<
      AssetClassApiResponse[],
      AssetClassesFactorsApiOptions
    >({
      query: ({ asOfDate, selectedAssetClass }) =>
        encodeURI(
          `/marketData/assetClasses/${selectedAssetClass}/factors?asOfDate=${asOfDate}`
        ),
    }),

    getAssetFactorsTimeSeries: builder.query<
      AssetFactorsSeries,
      AssetFactorsApiOptions
    >({
      queryFn: async (
        { valuationDate, selectedAssetClass, selectedAssetClassesFactor },
        _queryApi,
        _extraOptions,
        baseQuery
      ) => {
        const query = encodeURI(
          `/marketData/assetClasses/${selectedAssetClass}/factors/${selectedAssetClassesFactor}?valuationDate=${valuationDate}`
        );
        const result = await baseQuery({ url: query, responseHandler: 'text' });
        let transformedData;

        if (result.data && typeof result.data === 'string') {
          const cvsToJson = Papa.parse(result.data as string, {
            header: true,
          })?.data;

          transformedData = cvsToJson.map((data: any) => {
            const mapped = Object.keys(data)
              .map((key) => {
                if (new Date(key).getDate()) {
                  return { date: key, value: parseFloat(data[key]) };
                }
                return undefined;
              })
              .filter((res) => res);
            // eslint-disable-next-line @typescript-eslint/dot-notation
            return { [data['dimensions'] || ['Rate']]: mapped };
          });
        }
        if (result.error) {
          return result.error;
        }
        return { data: { timeseries: { timeSeriesData: transformedData } } };
      },
    }),
  }),
});

export const {
  useGetAssetClassesQuery,
  useGetAssetClassFactorsQuery,
  useGetAssetFactorsTimeSeriesQuery,
} = marketDataApi;
